import { useState } from 'react';
import { Quote, QuoteItem } from '../../../mocks/quotes';
import { crmLeads } from '../../../mocks/crm';

interface QuoteBuilderProps {
  initialQuote?: Quote | null;
  onClose: () => void;
  onSave: (quote: Partial<Quote>) => void;
}

interface DraftItem extends Omit<QuoteItem, 'id'> {
  id: string;
}

const categoryOptions = ['Accommodation', 'Flight', 'Cruise', 'Activity', 'Transfer', 'Insurance', 'Special Arrangement', 'Transport', 'Service', 'Dining'];
const unitOptions = ['night', 'ticket', 'person', 'package', 'day', 'cabin', 'pass', 'transfer', 'sector', 'event', 'policy'];

function generateId() {
  return `i${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function QuoteBuilder({ initialQuote, onClose, onSave }: QuoteBuilderProps) {
  const [step, setStep] = useState(1);
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(
    initialQuote ? { id: initialQuote.leadId, name: initialQuote.leadName, email: initialQuote.leadEmail, phone: initialQuote.leadPhone, service: initialQuote.leadService, destination: initialQuote.destination, travelDate: initialQuote.travelDate, pax: initialQuote.pax } : null
  );
  const [title, setTitle] = useState(initialQuote?.title ?? '');
  const [notes, setNotes] = useState(initialQuote?.notes ?? '');
  const [items, setItems] = useState<DraftItem[]>(initialQuote?.items ?? [
    { id: generateId(), description: '', category: 'Accommodation', qty: 1, unit: 'night', unitPrice: 0, total: 0 },
  ]);
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>(initialQuote?.discountType ?? 'percent');
  const [discountValue, setDiscountValue] = useState(initialQuote?.discountValue ?? 0);
  const [taxRate, setTaxRate] = useState(initialQuote?.taxRate ?? 5);
  const [validDays, setValidDays] = useState(10);
  const [terms, setTerms] = useState(initialQuote?.terms ?? 'This quote is valid for the specified validity period. A deposit is required to confirm the booking. Balance is due before the travel date. Cancellation fees apply as per supplier terms.');

  const filteredLeads = crmLeads.filter((l) =>
    l.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
    l.service.toLowerCase().includes(leadSearch.toLowerCase())
  ).filter((l) => l.status !== 'Won' && l.status !== 'Lost');

  const subtotal = items.reduce((s, i) => s + i.total, 0);
  const discountAmount = discountType === 'percent' ? (subtotal * discountValue) / 100 : discountValue;
  const taxBase = subtotal - discountAmount;
  const taxAmount = (taxBase * taxRate) / 100;
  const totalAmount = taxBase + taxAmount;

  const updateItem = (id: string, field: keyof DraftItem, value: string | number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === 'qty' || field === 'unitPrice') {
          updated.total = Number(updated.qty) * Number(updated.unitPrice);
        }
        return updated;
      })
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { id: generateId(), description: '', category: 'Accommodation', qty: 1, unit: 'night', unitPrice: 0, total: 0 },
    ]);
  };

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleSave = (sendNow: boolean) => {
    const validUntilDate = new Date();
    validUntilDate.setDate(validUntilDate.getDate() + validDays);
    onSave({
      leadId: selectedLead?.id ?? '',
      leadName: selectedLead?.name ?? '',
      leadEmail: selectedLead?.email ?? '',
      leadPhone: selectedLead?.phone ?? '',
      leadService: selectedLead?.service ?? '',
      destination: selectedLead?.destination ?? '',
      travelDate: selectedLead?.travelDate ?? '',
      pax: selectedLead?.pax ?? 1,
      title,
      items,
      subtotal,
      discountType,
      discountValue,
      discountAmount,
      taxRate,
      taxAmount,
      totalAmount,
      currency: 'AED',
      validUntil: validUntilDate.toISOString().slice(0, 10),
      status: sendNow ? 'Sent' : 'Draft',
      notes,
      terms,
    });
  };

  const steps = ['Select Lead', 'Quote Items', 'Review & Send'];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <i className="ri-draft-line text-base" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {initialQuote ? `Edit Quote · ${initialQuote.id}` : 'New Quote'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-1.5">
                    <button
                      onClick={() => i < step - 1 && setStep(i + 1)}
                      className={`flex items-center gap-1 text-xs font-medium transition-colors ${step === i + 1 ? 'text-amber-600' : i < step - 1 ? 'text-gray-500 cursor-pointer hover:text-amber-500' : 'text-gray-300'}`}
                    >
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${step === i + 1 ? 'bg-amber-500 text-white' : i < step - 1 ? 'bg-gray-300 text-gray-600' : 'border border-gray-200 text-gray-300'}`}>
                        {i < step - 1 ? <i className="ri-check-line text-xs" /> : i + 1}
                      </span>
                      <span className="hidden sm:inline">{s}</span>
                    </button>
                    {i < steps.length - 1 && <span className="text-gray-200 text-xs">›</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Select Lead */}
          {step === 1 && (
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">Select a lead to create a quote for, or start with a blank quote.</p>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search leads by name or service..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
                />
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredLeads.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => {
                      setSelectedLead({ id: lead.id, name: lead.name, email: lead.email, phone: lead.phone, service: lead.service, destination: lead.service, travelDate: lead.travelDate, pax: lead.pax });
                      if (!title) setTitle(lead.service + ' — Quote');
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left ${selectedLead?.id === lead.id ? 'border-amber-300 bg-amber-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{lead.name}</p>
                      <p className="text-xs text-gray-500 truncate">{lead.service}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-700">{lead.budget}</p>
                      <p className="text-xs text-gray-400">{lead.pax} pax · {lead.travelDate}</p>
                    </div>
                    {selectedLead?.id === lead.id && (
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-white flex-shrink-0">
                        <i className="ri-check-line text-xs" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {selectedLead && (
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Quote Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Maldives Honeymoon Package, 7 Nights"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Quote Items */}
          {step === 2 && (
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <input
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          placeholder="Service description (e.g. 4-Star Hotel Paris, 3 nights)"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
                        />
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 cursor-pointer flex-shrink-0 transition-colors"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <select
                        value={item.category}
                        onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                        className="col-span-2 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
                      >
                        {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                        <input
                          type="number"
                          min={1}
                          value={item.qty}
                          onChange={(e) => updateItem(item.id, 'qty', Number(e.target.value))}
                          className="w-12 px-2 py-2 text-sm outline-none text-center"
                        />
                        <select
                          value={item.unit}
                          onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                          className="flex-1 px-1 py-2 text-xs border-l border-gray-200 outline-none bg-white cursor-pointer"
                        >
                          {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">AED</span>
                        <input
                          type="number"
                          min={0}
                          value={item.unitPrice}
                          onChange={(e) => updateItem(item.id, 'unitPrice', Number(e.target.value))}
                          className="w-full pl-10 pr-2 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <span className="text-sm font-semibold text-gray-700">
                        Total: AED {item.total.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
                <button
                  onClick={addItem}
                  className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-amber-300 hover:text-amber-500 cursor-pointer transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-add-circle-line" /> Add Service Item
                </button>
              </div>

              {/* Discount & Tax */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Discount Type</label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as 'percent' | 'fixed')}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed (AED)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                    Discount {discountType === 'percent' ? '(%)' : '(AED)'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(Number(e.target.value))}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">VAT Rate (%)</label>
                  <select
                    value={taxRate}
                    onChange={(e) => setTaxRate(Number(e.target.value))}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    {[0, 5, 10, 15, 20].map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>AED {subtotal.toLocaleString()}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Discount ({discountType === 'percent' ? `${discountValue}%` : 'Fixed'})</span>
                    <span>-AED {discountAmount.toLocaleString()}</span>
                  </div>
                )}
                {taxAmount > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>VAT ({taxRate}%)</span>
                    <span>+AED {taxAmount.toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold text-gray-800 pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span className="text-amber-600">AED {totalAmount.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="p-6 space-y-5">
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500 text-white flex-shrink-0">
                  <i className="ri-file-text-line" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{title || 'Untitled Quote'}</p>
                  <p className="text-xs text-gray-500">{selectedLead?.name} · {selectedLead?.email} · AED {totalAmount.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Quote Validity</label>
                  <select
                    value={validDays}
                    onChange={(e) => setValidDays(Number(e.target.value))}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    <option value={7}>7 days</option>
                    <option value={10}>10 days</option>
                    <option value={14}>14 days</option>
                    <option value={21}>21 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Number of Items</label>
                  <div className="px-3 py-2.5 text-sm border border-gray-100 rounded-lg bg-gray-50 text-gray-700">
                    {items.length} service {items.length === 1 ? 'item' : 'items'}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Internal Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Internal notes for your team (not shown on quote)..."
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Terms &amp; Conditions</label>
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Terms and conditions shown on the quote..."
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 resize-none"
                />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-4 text-center text-sm">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Total</p>
                  <p className="font-bold text-amber-600">AED {totalAmount.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Valid For</p>
                  <p className="font-bold text-gray-800">{validDays} days</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Pax</p>
                  <p className="font-bold text-gray-800">{selectedLead?.pax ?? 1}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line" /> Back
            </button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 && !selectedLead}
              className="flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue <i className="ri-arrow-right-line" />
            </button>
          ) : (
            <>
              <button
                onClick={() => handleSave(false)}
                className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-save-line" /> Save Draft
              </button>
              <button
                onClick={() => handleSave(true)}
                className="flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-send-plane-fill" /> Send Quote
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
